import { z } from "zod";

export const INQUIRY_STATUSES = ["RECEIVED", "IN_PROGRESS", "ANSWERED", "CLOSED"] as const;

const isoDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isValidInquiryDate(value: string): boolean {
  if (!value) return true;
  const match = isoDatePattern.exec(value);
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (year < 1900 || year > 9998) return false;
  const parsed = new Date(Date.UTC(year, month - 1, day));
  return parsed.getUTCFullYear() === year
    && parsed.getUTCMonth() === month - 1
    && parsed.getUTCDate() === day;
}

const positiveId = (label: string) => z.string().refine(
  (value) => {
    const normalized = value.trim();
    return normalized === "" || (/^\d+$/.test(normalized)
      && Number.isSafeInteger(Number(normalized))
      && Number(normalized) > 0);
  },
  `${label}는 1 이상의 숫자로 입력해 주세요.`,
);

export const inquirySearchFormSchema = z.object({
  status: z.union([z.literal(""), z.enum(INQUIRY_STATUSES)]),
  memberId: positiveId("회원 ID"),
  assignedAdminLoginId: z.string().trim().max(100, "담당자 ID는 100자 이하로 입력해 주세요."),
  from: z.string().refine(isValidInquiryDate, "유효한 접수 시작일을 선택해 주세요."),
  to: z.string().refine(isValidInquiryDate, "유효한 접수 종료일을 선택해 주세요."),
}).superRefine((values, context) => {
  if (values.from && values.to && values.from > values.to) {
    context.addIssue({
      code: "custom",
      message: "접수 종료일은 시작일보다 빠를 수 없습니다.",
      path: ["to"],
    });
  }
});

export const inquiryReplyFormSchema = z.object({
  content: z.string().trim().min(1, "답변을 입력해 주세요.").max(4_000, "답변은 4,000자 이하로 입력해 주세요."),
});

export type InquirySearchFormValues = z.infer<typeof inquirySearchFormSchema>;
export type InquiryReplyFormValues = z.infer<typeof inquiryReplyFormSchema>;

export function parseInquiryStatus(value: string | null): InquirySearchFormValues["status"] {
  if (!value || value === "ALL") return "";
  const parsed = z.enum(INQUIRY_STATUSES).safeParse(value);
  return parsed.success ? parsed.data : "";
}

export function parseInquiryId(value: string | null): string {
  if (!value || !/^\d+$/.test(value)) return "";
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? String(parsed) : "";
}

export function parseInquiryDate(value: string | null): string {
  return value && isValidInquiryDate(value) ? value : "";
}
